import { useEffect, useRef, useState } from "react";
import {
  FiBell,
  FiCalendar,
  FiGlobe,
  FiImage,
  FiInfo,
  FiMail,
  FiPhone,
  FiVideo,
} from "react-icons/fi";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";
import { API_URL } from "../../config/app-config";

const ACCENT = "#161616";

export default function CalendarTabs({
  eventDetails,
  contact,
  clarityService,
  viewClarityService,
  isPreview,
}) {
  const [activeTab, setActiveTab] = useState("About");
  const [tabs, setTabs] = useState([]);
  const [galleryImage, setGalleryImage] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [state, setState] = useState({
    serviceAnnouncement: [],
    serviceProviderVideo: [],
  });
  const [isInitialRender, setIsInitialRender] = useState(true);

  const tabContentRef = useRef(null);

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }

    const scrollContainer = tabContentRef.current;
    if (scrollContainer) {
      requestAnimationFrame(() => {
        scrollContainer.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    }
  }, [activeTab]);

  const updateState = (name, value) => {
    setState((previous) => ({ ...previous, [name]: value }));
  };

  useEffect(() => {
    const needTabs = [];
    if (eventDetails.description) needTabs.push("About");
    if (eventDetails.phoneNumberShow || eventDetails.emailShow)
      needTabs.push("Contact");

    if (eventDetails.serviceGalleryImage) {
      needTabs.push("Photos");

      setGalleryImage(
        isPreview
          ? eventDetails.serviceGalleryImage
          : eventDetails.serviceGalleryImage
              .split(",")
              .map((item) => `${API_URL}auth/image/${item.trim()}`)
      );
    }

    if (eventDetails.serviceProviderVideo) {
      needTabs.push("Videos");
      updateState(
        "serviceProviderVideo",
        isPreview
          ? eventDetails.serviceProviderVideo
          : eventDetails.serviceProviderVideo
              .split(",")
              .map((item) => item.trim())
      );
    }

    if (eventDetails.serviceAnnouncement) {
      needTabs.push("Announcements");
      updateState(
        "serviceAnnouncement",
        isPreview
          ? eventDetails.serviceAnnouncement
          : eventDetails.serviceAnnouncement
              .split(",")
              .map((item) => `${API_URL}auth/image/${item.trim()}`)
      );
    }

    setTabs(needTabs);
  }, [eventDetails]);

  const TabButton = ({ tab }) => (
    <button
      className={`c-tab ${activeTab === tab ? "active" : ""}`}
      onClick={() => setActiveTab(tab)}
      style={{
        padding: "10px 14px",
        border: "none",
        borderRadius: 999,
        background: activeTab === tab ? "#f5f5f5" : "transparent",
        color: activeTab === tab ? ACCENT : "#8a8a92",
        fontWeight: "600",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        cursor: "pointer",
        transition: "all 0.15s ease",
        whiteSpace: "nowrap",
      }}
    >
      {
        {
          About: <FiInfo size={16} />,
          Contact: <FiMail size={16} />,
          Photos: <FiImage size={16} />,
          Videos: <FiVideo size={16} />,
          Announcements: <FiBell size={16} />,
        }[tab]
      }
      {tab}
    </button>
  );

  const tabContentStyle = {
    padding: "22px 24px 24px",
    flex: 1,
    overflowY: "auto",
    maxHeight: "500px",
    transform: "translateZ(0)",
    willChange: "scroll-position",
    backfaceVisibility: "hidden",
  };

  const contactRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    background: "#fafafa",
    borderRadius: "14px",
  };

  return (
    <section
      className="calendar-tabs"
      style={{
        background: "#ffffff",
        borderRadius: "24px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
      }}
    >
      {isOpen && (
        <Lightbox
          mainSrc={
            (activeTab === "Photos" ? galleryImage : state.serviceAnnouncement)[
              photoIndex
            ]
          }
          nextSrc={
            (activeTab === "Photos" ? galleryImage : state.serviceAnnouncement)[
              (photoIndex + 1) %
                (activeTab === "Photos"
                  ? galleryImage.length
                  : state.serviceAnnouncement.length)
            ]
          }
          prevSrc={
            (activeTab === "Photos" ? galleryImage : state.serviceAnnouncement)[
              (photoIndex +
                (activeTab === "Photos"
                  ? galleryImage.length
                  : state.serviceAnnouncement.length) -
                1) %
                (activeTab === "Photos"
                  ? galleryImage.length
                  : state.serviceAnnouncement.length)
            ]
          }
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex(
              (photoIndex +
                (activeTab === "Photos"
                  ? galleryImage.length
                  : state.serviceAnnouncement.length) -
                1) %
                (activeTab === "Photos"
                  ? galleryImage.length
                  : state.serviceAnnouncement.length)
            )
          }
          onMoveNextRequest={() =>
            setPhotoIndex(
              (photoIndex + 1) %
                (activeTab === "Photos"
                  ? galleryImage.length
                  : state.serviceAnnouncement.length)
            )
          }
        />
      )}

      <div
        className="c-tabs"
        style={{
          display: "flex",
          overflowX: "auto",
          padding: "14px 16px 0",
          borderBottom: "1px solid #f0f0f0",
        }}
      >
        <div style={{ display: "flex", gap: "4px", paddingBottom: 12 }}>
          {tabs.map((tab) => (
            <TabButton key={tab} tab={tab} />
          ))}
        </div>
      </div>

      <div
        ref={tabContentRef}
        className="c-tab-content"
        style={tabContentStyle}
      >
        {activeTab === "Photos" || activeTab === "Announcements" ? (
          <div className="c-gallery">
            {activeTab === "Announcements" &&
              eventDetails?.announcementContent && (
                <div
                  style={{
                    marginBottom: "24px",
                    padding: "16px",
                    background: "#fafafa",
                    borderRadius: "14px",
                  }}
                >
                  <p
                    style={{
                      color: "#2d3748",
                      lineHeight: "1.6",
                      margin: 0,
                      fontSize: "14px",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: eventDetails.announcementContent,
                    }}
                  />
                </div>
              )}

            <div
              className="c-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
                gap: "12px",
              }}
            >
              {(activeTab === "Photos"
                ? galleryImage
                : state.serviceAnnouncement
              ).map((data, index) => (
                <div
                  key={index}
                  style={{
                    position: "relative",
                    borderRadius: "14px",
                    overflow: "hidden",
                    cursor: "pointer",
                    aspectRatio: "1/1",
                    transition: "transform 0.2s ease",
                    background: "#fafafa",
                  }}
                  onClick={() => {
                    if (isPreview) return;
                    setPhotoIndex(index);
                    setIsOpen(true);
                  }}
                >
                  <img
                    src={data}
                    alt={`${activeTab.slice(0, -1)} ${index + 1}`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/placeholder-image.jpg";
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : activeTab === "Videos" ? (
          <div className="c-gallery">
            <div className="c-grid" style={{ display: "grid", gap: "1.5rem" }}>
              {state.serviceProviderVideo.map((data, index) => {
                const videoUrl = isPreview
                  ? data
                  : `${API_URL}auth/video/${data}`;

                return (
                  <div key={index} style={{ width: "100%" }}>
                    <video
                      style={{
                        width: "100%",
                        height: "200px",
                        borderRadius: "14px",
                        backgroundColor: "#000",
                      }}
                      controls
                      onError={(e) => {
                        console.error("Video error:", e.target.error);
                        e.target.style.display = "none";
                        e.target.insertAdjacentHTML(
                          "afterend",
                          `
                        <div style="
                          width: 100%;
                          height: 200px;
                          background: #1a1a1a;
                          color: white;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          border-radius: 14px;
                        ">
                          Video unavailable
                        </div>
                      `
                        );
                      }}
                    >
                      <source src={videoUrl} />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                );
              })}
            </div>
          </div>
        ) : activeTab === "Contact" ? (
          <div
            className="contact-list"
            style={{ display: "grid", gap: "12px" }}
          >
            {eventDetails?.emailShow && contact.email && (
              <div style={contactRowStyle}>
                <FiMail size={20} color="#4a5568" />
                <div>
                  <div style={{ fontSize: "12px", color: "#a0a0a8" }}>Email</div>
                  <div style={{ color: "#161616", fontWeight: "500" }}>{contact.email}</div>
                </div>
              </div>
            )}

            {eventDetails?.phoneNumberShow && contact.phoneNumber && (
              <div style={contactRowStyle}>
                <FiPhone size={20} color="#4a5568" />
                <div>
                  <div style={{ fontSize: "12px", color: "#a0a0a8" }}>Phone</div>
                  <div style={{ color: "#161616", fontWeight: "500" }}>{contact.phoneNumber}</div>
                </div>
              </div>
            )}

            {eventDetails.countryName && (
              <div style={contactRowStyle}>
                <FiGlobe size={20} color="#4a5568" />
                <div>
                  <div style={{ fontSize: "12px", color: "#a0a0a8" }}>Country</div>
                  <div style={{ color: "#161616", fontWeight: "500" }}>{eventDetails.countryName}</div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gap: "20px" }}>
            <div>
              <h3
                style={{
                  color: "#161616",
                  margin: "0 0 8px 0",
                  fontSize: "16px",
                  fontWeight: "700",
                }}
              >
                Service Description
              </h3>
              <div
                style={{
                  color: "#718096",
                  lineHeight: "1.6",
                  fontSize: "14px",
                }}
                dangerouslySetInnerHTML={{ __html: eventDetails.description }}
              />
            </div>

            {clarityService && eventDetails.demoSessionAvailable && (
              <button
                onClick={() => viewClarityService(clarityService, "clarity")}
                style={{
                  background: ACCENT,
                  color: "#fff",
                  padding: "14px 24px",
                  borderRadius: "999px",
                  border: "none",
                  fontWeight: "600",
                  fontSize: 13,
                  cursor: "pointer",
                  display: "flex",
                  gap: "10px",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiCalendar size={16} />
                Book Free Clarity Session
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}